import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordValidator from 'password-validator';
import axios from 'axios';
import cn from 'classnames';
import { debounce } from 'lodash';
import { validate as isValidEmail } from 'email-validator';
import { Pathname } from 'Routes';
import { dataFetcher, endpoints, baseUrl } from 'Api';
import { AuthContext, AppContext } from 'Context';
import { Button, Snackbar } from 'Components';
import RotatingLoader from 'Components/RotatingLoader';
import LightRays from 'Components/reactbits/LightRays';
import { Input } from '../components/input-field';
import { AuthPage } from '../components/page';
import theme from '../components/auth-theme.module.css';
import authStyles from '../auth.module.css';
import styles from './index.module.css';

const passwordSchema = new PasswordValidator()
  .is()
  .min(12)
  .is()
  .max(30)
  .has()
  .symbols(1)
  .has()
  .digits(1)
  .has()
  .uppercase(1)
  .lowercase(1);
const defaultSnackbar = {
  isVisible: false,
  type: undefined,
  message: undefined,
};

// ChottuLink smart link used for the gift-receiver "Open in Projector App"
// hand-off. Building it as a plain URL (no API key needed) lets ChottuLink own
// all the routing:
//   • app installed  → opens projector://setpassword?... — the app's existing
//                      URI-scheme handling picks up the gift params
//   • not installed  → App Store (via the App Store ID set in the ChottuLink
//                      dashboard), then the ChottuLink iOS SDK hands the params
//                      to the app on first launch (deferred deep linking)
//   • desktop/no-app → the link's configured web fallback
const CHOTTULINK_DOMAIN = 'projectorapp.chottu.link';
const APP_DEEPLINK_PATH = 'giftreceiver'; // → https://projectorapp.chottu.link/giftreceiver?...

const isMobileDevice = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
  const isSmallScreen = window.innerWidth <= 767;
  return isMobileUA || isSmallScreen;
};

// The login screen's backdrop: volumetric light rays fanning down over the
// near-black base, props identical to /newSign-in so both read as one surface.
const RaysBackdrop = ({ className }) => (
  <div className={className || theme.rays} aria-hidden="true">
    <LightRays
      raysOrigin="top-center"
      raysColor="#ffffff"
      raysSpeed={0.9}
      lightSpread={0.85}
      rayLength={1.25}
      fadeDistance={1.1}
      saturation={1}
      followMouse
      mouseInfluence={0.08}
      noiseAmount={0.08}
      distortion={0.04}
    />
  </div>
);

// Same five password rules the page has always shown, in the login card's tick
// list instead of a bulleted box. Content and the conditions behind each row
// are unchanged.
const PASSWORD_RULES = [
  { text: '12 - 30 Characters', isMet: (p) => p.length >= 12 && p.length <= 30 },
  { text: 'Minimum of one uppercase', isMet: (p) => /[A-Z]/.test(p) },
  { text: 'Minimum of one lowercase', isMet: (p) => /[a-z]/.test(p) },
  { text: 'Minimum of one number', isMet: (p) => /[0-9]/.test(p) },
  { text: 'Minimum of one special character', isMet: (p) => /[!@#$%^&*()_+|~=`{}[\]:";'<>?,./]/.test(p) },
];

const PasswordRules = ({ password }) => (
  <div className={cn(theme.hintList, styles.rules)}>
    <p className={styles.rulesTitle}>Password Requirements:</p>
    {PASSWORD_RULES.map(({ text, isMet }) => {
      const met = Boolean(password) && isMet(password);
      return (
        <div key={text} className={cn(styles.rule, { [styles.ruleMet]: met })}>
          <svg
            className={styles.ruleIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {met ? <path d="M20 6 9 17l-5-5" /> : <circle cx="12" cy="12" r="9" />}
          </svg>
          <span>{text}</span>
        </div>
      );
    })}
  </div>
);

export const SetPassword = () => {
  const navigate = useNavigate();
  const confirmPasswordRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { setToken, forceAutoSignin } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [temporaryToken, setTemporaryToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [noPassword, setNoPassword] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Gift receive flow state
  const isGiftFlow = searchParams.get('gift') === 'true';
  // Quick-user links (no gift params) get the same confetti/fireworks
  // celebration as the gift flow, over the page's own layout.
  const isQuickUserFlow = !isGiftFlow;
  const isExistingUser = searchParams.get('isExistingUser') === 'true';
  const giftFromName = searchParams.get('fromName') || 'Someone special';
  // Parse uid from URL — handle both &uid= and %uid= (malformed separator)
  const giftUid = isExistingUser
    ? (searchParams.get('uid') || (() => {
      const match = window.location.search.match(/[%&]uid=([^&]*)/);
      return match ? decodeURIComponent(match[1]) : '';
    })())
    : '';
  const existingUserEmail = isExistingUser ? (searchParams.get('email') || '') : '';
  const [userEmail, setUserEmail] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [showGiftPopup, setShowGiftPopup] = useState(isGiftFlow);
  const [showCelebration, setShowCelebration] = useState(isGiftFlow);
  const [celebrationFading, setCelebrationFading] = useState(false);
  const [giftContentStep, setGiftContentStep] = useState('celebration');
  const [confirmEmailInput, setConfirmEmailInput] = useState('');
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [showOpenAppPopup, setShowOpenAppPopup] = useState(false);
  const [showProjectorVideo, setShowProjectorVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const videoRef = useRef(null);
  const projectorVideoUrl = 'https://projector-optimized.s3.us-west-2.amazonaws.com/production/background-videos/Web_lq.mp4';
  const emailConfirmed = confirmEmailInput && userEmail && confirmEmailInput.trim().toLowerCase() === userEmail.trim().toLowerCase();

  // Debounced email match check — fires after user stops typing
  const debouncedEmailMatch = useCallback(
    debounce((typed) => {
      if (!typed) {
        setEmailVerifying(false);
        setEmailMismatch(false);
        return;
      }
      const matches = userEmail && typed.trim().toLowerCase() === userEmail.trim().toLowerCase();
      setEmailMismatch(!matches);
      setEmailVerifying(false);
    }, 800),
    [userEmail]
  );

  // Debounced password match check — fires after user stops typing
  const debouncedPasswordMatch = useCallback(
    debounce((confirmVal, currentPassword) => {
      if (confirmVal && currentPassword && confirmVal !== currentPassword) {
        setSnackbar({ isVisible: true, type: 'error', message: 'Passwords do not match' });
      } else {
        setSnackbar(defaultSnackbar);
      }
    }, 800),
    []
  );

  // Debounced email check - calls signinEmail API to verify if email is already used
  const debouncedEmailCheck = useCallback(
    debounce((emailToCheck) => {
      if (!isValidEmail(emailToCheck)) {
        setCheckingEmail(false);
        setEmailExists(null);
        return;
      }
      setCheckingEmail(true);
      dataFetcher(endpoints.signinEmail, { email: emailToCheck })
        .then(({ success }) => {
          setEmailExists(success);
          setCheckingEmail(false);
        })
        .catch(() => {
          setEmailExists(null);
          setCheckingEmail(false);
        });
    }, 750),
    []
  );

  // Reset zoom and scroll before paint so the celebration displays correctly
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const original = viewportMeta?.getAttribute('content');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no');
    }
    return () => {
      if (viewportMeta && original) {
        viewportMeta.setAttribute('content', original);
      }
    };
  }, []);

  // Track gift receive page view in Google Analytics
  useEffect(() => {
    if (isGiftFlow && window.gtag) {
      window.gtag('event', 'Gift Receiver - Page Opened', {
        user_type: isExistingUser ? 'Existing User' : 'New User',
        gifted_by: giftFromName,
      });
    }
  }, [isGiftFlow, isExistingUser, giftFromName]);

  // Persist sender name so /gift-content can show "messages from {name}"
  // (URL params don't carry through avatar → gift-content).
  useEffect(() => {
    if (!isGiftFlow) return;
    if (giftFromName && giftFromName !== 'Someone special') {
      localStorage.setItem('giftFromName', giftFromName);
    }
  }, [isGiftFlow, giftFromName]);

  // Lock body scroll when gift popup is open so background doesn't scroll
  useEffect(() => {
    if (showGiftPopup) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showGiftPopup]);

  // Quick-user flow: same confetti/fireworks celebration as the gift flow, fired
  // once the token check has finished. No popup — the page keeps its own layout.
  useEffect(() => {
    if (!isQuickUserFlow || isLoading) return;
    setShowCelebration(true);
  }, [isQuickUserFlow, isLoading]);

  // Auto-hide celebration particles — fade out then show projector video
  useEffect(() => {
    if (!showCelebration) return;

    const fadeTimer = setTimeout(() => {
      setCelebrationFading(true);
    }, 7000);

    const removeTimer = setTimeout(() => {
      setShowCelebration(false);
      setCelebrationFading(false);
    }, 8000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [showCelebration]);

  // Start video playback when ready — only after user has interacted (clicked email field)
  const videoPlayStarted = useRef(false);
  useEffect(() => {
    if (showProjectorVideo && videoReady && userInteracted && videoRef.current && !videoPlayStarted.current) {
      videoPlayStarted.current = true;
      const el = videoRef.current;
      el.play().then(() => {
        setVideoMuted(false);
      }).catch(() => {
        Object.assign(el, { muted: true });
        setVideoMuted(true);
        el.play().catch(() => {});
      });
    }
  }, [showProjectorVideo, videoReady, userInteracted]);

  // Show "Open in App" popup on mobile immediately when page loads
  useEffect(() => {
    if (isGiftFlow && isMobileDevice()) {
      setShowOpenAppPopup(true);
    }
  }, [isGiftFlow]);

  // Extract token from URL and verify with API
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setTemporaryToken(token);
      // console.log('Token extracted from URL:', token);

      // Call signinEmail API to check if user needs to set password
      // Using direct axios call to avoid interceptor overwriting the token
      setIsLoading(true);
      axios
        .post(`${baseUrl}${endpoints.signinEmail}`, { token })
        .then(({ data }) => {
          // console.log('========== signinEmail API Response ==========');
          // console.log('Full Response:', data);
          // console.log('Success:', data.success);
          // console.log('No Password:', data.no_password);
          // console.log('Message:', data.message);
          // console.log('==============================================');

          if (data.success && data.no_password !== undefined) {
            setNoPassword(data.no_password);
          }
          // Fetch user's current email from profile
          if (isGiftFlow) {
            axios
              .post(`${baseUrl}${endpoints.getMyProfile}`, { token })
              .then(({ data: profileData }) => {
                const email = profileData?.data?.email || '';
                setUserEmail(email);
                setNewEmail(email);
              })
              .catch(() => {});
          }
          setIsLoading(false);
        })
        .catch((error) => {
          // console.error('========== signinEmail API Error ==========');
          // console.error('Error:', error);
          // console.error('Error Response:', error.response?.data);
          // console.error('===========================================');
          setIsLoading(false);
          setSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Invalid or expired token',
          });
        });
    } else {
      // console.log('No token found in URL');
      setIsLoading(false);
    }
  }, [searchParams]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const isPasswordValid = passwordSchema.validate(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const passwordHealth = (
    password
      ? password.length < 12 || !hasUpperCase
        ? 'weak'
        : passwordSchema.validate(password)
          ? password.length >= 12
            ? 'strong'
            : 'medium'
          : 'weak'
      : ''
  );

  const handleNext = async (e) => {
    e.preventDefault();

    if (!password) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Please enter a password',
      });
      return;
    }

    // Existing user gift flow: login with password directly
    if (isExistingUser && isGiftFlow) {
      setSubmitLoading(true);
      try {
        const loginResult = await dataFetcher(endpoints.signin, {
          email: existingUserEmail,
          password,
        });

        if (loginResult?.success && loginResult?.data?.token) {
          setToken(loginResult.data.token);

          // Store uploader token if available
          if (loginResult?.api_response?.access) {
            localStorage.setItem('uploader_token', loginResult.api_response.access);
            try {
              const jwtPayload = JSON.parse(atob(loginResult.api_response.access.split('.')[1]));
              if (jwtPayload.exp) {
                localStorage.setItem('uploader_token_expiry', (jwtPayload.exp * 1000).toString());
              }
            } catch (jwtErr) { console.warn('[GIFT_FLOW] Failed to parse uploader token expiry:', jwtErr); }
          }
          if (loginResult?.api_response?.uuid) {
            localStorage.setItem('user_uuid', loginResult.api_response.uuid);
          }

          // Track existing user gift login in Google Analytics
          if (window.gtag) {
            window.gtag('event', 'Gift Receiver - Logged In', {
              user_type: 'Existing User',
            });
          }

          setAppSnackbar({
            message: 'Logged in successfully',
            isVisible: true,
          });

          setTimeout(() => {
            forceAutoSignin();
            navigate(Pathname.giftFlow.content);
          }, 500);
        } else {
          setSnackbar({
            isVisible: true,
            type: 'error',
            message: loginResult?.message || 'Invalid password. Please try again.',
          });
          setSubmitLoading(false);
        }
      } catch (loginErr) {
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: loginErr?.response?.data?.message || loginErr?.message || 'Login failed. Please try again.',
        });
        setSubmitLoading(false);
      }
      return;
    }

    if (!isPasswordValid) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password does not meet requirements',
      });
      return;
    }

    if (password.length > 30) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password cannot exceed 30 characters',
      });
      return;
    }

    if (!passwordsMatch) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Passwords do not match',
      });
      return;
    }

    // Call setPassword API
    // Using direct axios call to avoid interceptor overwriting the token
    setSubmitLoading(true);
    try {
      const payload = {
        token: temporaryToken,
        password,
      };

      const { data } = await axios.post(`${baseUrl}${endpoints.setPassword}`, payload);

      if (data.success && data.data?.token) {
        // Store token in localStorage
        setToken(data.data.token);

        // Track new user password set in Google Analytics
        if (isGiftFlow && window.gtag) {
          window.gtag('event', 'Gift Receiver - Password Set', {
            user_type: 'New User',
          });
        }

        // Show success message (matching login/signup flow). The app-level toast
        // is what survives the navigate() below, so it carries the page's bottom
        // placement rather than dropping in from the top mid-transition.
        setAppSnackbar({
          message: 'Password set successfully',
          isVisible: true,
          placement: 'bottom-edge',
        });

        // Check if new user from API response (matching social login logic)
        const isNewUser = data.new_user || data.data?.is_new_user;

        // Wait a moment, then trigger auto sign-in and navigate
        setTimeout(() => {
          forceAutoSignin();

          // Navigate to avatar for new users (matching social login flow)
          // Navigate to access for existing users
          if (isNewUser) {
            navigate(`${Pathname.authentication.avatar}${isGiftFlow ? '?gift=true' : ''}`, { state: { fromSetPassword: true, isGiftFlow } });
          } else {
            // Force page reload to ensure proper authentication state
            window.location.href = '/access';
          }
        }, 500);
      } else {
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: data.message || 'Failed to set password',
        });
        setSubmitLoading(false);
      }
    } catch (error) {
      // console.error('========== Set Password API Error ==========');
      // console.error('Error:', error);
      // console.error('Error Response:', error.response?.data);
      // console.error('============================================');

      setSnackbar({
        isVisible: true,
        type: 'error',
        message: error.response?.data?.message || (typeof error.message === 'string' ? error.message : 'An error occurred while setting password'),
      });
      setSubmitLoading(false);
    }
  };

  const handlePasswordInput = (event) => {
    const inputPassword = event.target.value;
    if (inputPassword.length > 30) {
      setPassword(inputPassword.slice(0, 30));
    } else {
      setPassword(inputPassword);
    }
  };

  // Transition gift popup content from celebration → password form
  const handleDismissGiftPopup = () => {
    setShowCelebration(false);
    setCelebrationFading(false);
    setGiftContentStep('password');
    setTimeout(() => {
      window.scrollTo(0, 0);
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no');
      }
    }, 100);
  };

  // Dismiss projector video and continue to email confirmation
  const handleDismissProjectorVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setShowProjectorVideo(false);
  };

  // Hand off to the native app via a ChottuLink smart link. ChottuLink owns all
  // the routing (open app / App Store + deferred deep link / web fallback), so
  // we no longer build the raw projector:// URL or the visibilitychange + store
  // fallback by hand. Pass the RAW query string (do NOT round-trip through
  // URLSearchParams) so malformed separators (e.g. `%uid=`) survive intact —
  // URLSearchParams would re-encode the `%` and bury `uid` inside the previous
  // param's value, leaving the app with no way to recover it. The params are
  // forwarded so the app lands in the right gift session — even after a fresh
  // install (deferred deep linking).
  const handleOpenApp = () => {
    const query = window.location.search.replace(/^\?/, '');
    const smartLink = `https://${CHOTTULINK_DOMAIN}/${APP_DEEPLINK_PATH}${query ? `?${query}` : ''}`;
    window.location.href = smartLink;
  };

  // Dismiss "Open in App" popup and continue in browser
  const handleContinueInBrowser = () => {
    setShowOpenAppPopup(false);
  };

  // Existing user: set userEmail and giftUid from URL params on load
  useEffect(() => {
    if (isExistingUser && isGiftFlow) {
      if (existingUserEmail) setUserEmail(existingUserEmail);
      if (giftUid) localStorage.setItem('giftUid', giftUid);
      localStorage.setItem('existingUser', 'true');
    }
  }, [isExistingUser, isGiftFlow, existingUserEmail, giftUid]);

  // Auto-advance to password step when email is confirmed
  useEffect(() => {
    if (emailConfirmed && giftContentStep === 'celebration' && !emailVerifying) {
      const timer = setTimeout(() => {
        handleDismissGiftPopup();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [emailConfirmed, giftContentStep, emailVerifying]);

  // Confirm email → call API if email changed, then proceed to set password
  const [changingEmail, setChangingEmail] = useState(false);
  const handleEmailConfirm = async () => {
    const emailChanged = isEditingEmail && newEmail !== userEmail && isValidEmail(newEmail);
    if (emailChanged) {
      setChangingEmail(true);
      try {
        await axios.post(`${baseUrl}${endpoints.changeEmailBeforPasswordSet}`, {
          token: temporaryToken,
          email: newEmail,
        });
        setChangingEmail(false);
        setShowEmailConfirm(false);
      } catch (error) {
        setChangingEmail(false);
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: error.response?.data?.message || 'Failed to update email',
        });
      }
    } else {
      setShowEmailConfirm(false);
    }
  };

  // Render email confirmation screen (before celebration)
  const renderEmailConfirmScreen = () => {
    if (!isGiftFlow) return null;

    return (
      <div className={`${styles.emailConfirmOverlay} ${showEmailConfirm ? styles.emailConfirmOverlayVisible : ''}`}>
        {/* Ambient background glow orbs */}
        <div className={`${styles.emailConfirmGlow} ${styles.emailConfirmGlow1}`} />
        <div className={`${styles.emailConfirmGlow} ${styles.emailConfirmGlow2}`} />

        <div className={styles.emailConfirmCard}>
          {/* Animated top accent line */}
          <div className={styles.emailConfirmAccent} />

          <div className={styles.emailConfirmContent}>
            {/* Sender badge */}
            {/* <div className={styles.emailConfirmFromBadge}>
              <span className={styles.emailConfirmFromLabel}>From</span>
              <span className={styles.emailConfirmFromName}>{giftFromName}</span>
            </div> */}

            <h1 className={styles.emailConfirmTitle}>
              {`${giftFromName} Has Gifted You Projector Memories!`}
            </h1>
            <p className={styles.emailConfirmDescription}>
              {`${giftFromName} has sent Projector Memories to ${userEmail || 'your email'}. Would you like to continue with this email or use a different one?`}
            </p>

            {/* Email display or edit */}
            <div className={styles.emailConfirmInputWrap}>
              <label className={styles.emailConfirmLabel}>Deliver to</label>
              {isEditingEmail ? (
                <div className={styles.emailConfirmInputRow}>
                  <input
                    type="email"
                    className={styles.emailConfirmInput}
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailExists(null);
                      if (isValidEmail(e.target.value)) {
                        setCheckingEmail(true);
                        debouncedEmailCheck(e.target.value);
                      } else {
                        setCheckingEmail(false);
                      }
                    }}
                    placeholder="Enter your email address"
                    autoFocus
                  />
                  {checkingEmail && (
                    <div className={styles.emailSpinner}>
                      <div className={styles.emailSpinnerRing} />
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.emailConfirmEmailDisplay}>
                  {userEmail || 'your current email'}
                </div>
              )}
              {isEditingEmail && emailExists === true && (
                <p className={styles.emailExistsMsg}>An account with this email already exists. Please use a different email.</p>
              )}
              <button
                type="button"
                className={styles.emailConfirmChangeLink}
                onClick={() => {
                  setIsEditingEmail(!isEditingEmail);
                  if (isEditingEmail) {
                    setNewEmail(userEmail);
                    setEmailExists(null);
                    setCheckingEmail(false);
                  }
                }}
              >
                {isEditingEmail ? 'Cancel' : 'Use a different email'}
              </button>
            </div>

            <button
              type="button"
              className={styles.emailConfirmButton}
              onClick={handleEmailConfirm}
              disabled={changingEmail || (isEditingEmail && newEmail !== userEmail && (checkingEmail || emailExists === true || !isValidEmail(newEmail)))}
            >
              {changingEmail ? 'Updating...' : checkingEmail ? 'Checking...' : isEditingEmail && newEmail !== userEmail ? 'Confirm & Continue' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render gift celebration overlay + sliding popup
  const renderGiftCelebration = () => {
    if (!isGiftFlow && !isQuickUserFlow) return null;

    return (
      <>
        {/* Full-page loader for gift flow password submit - must be outside giftPopup
            because giftPopup's CSS transform creates a new containing block that
            prevents position:fixed from covering the full viewport */}
        {giftContentStep === 'password' && submitLoading && <RotatingLoader dark />}
        {/* Celebration confetti & fireworks */}
        {showCelebration && (
          <div className={`${styles.celebrationCanvas} ${celebrationFading ? styles.celebrationCanvasFading : ''}`}>
            {/* Confetti pieces — reduced on mobile for performance */}
            {Array.from({ length: window.innerWidth <= 767 ? 60 : 300 }, (_, i) => {
              const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6b9d', '#c9b1ff'];
              const fallClasses = [
                '', styles.confettiFall2, styles.confettiFall3,
                styles.confettiFall4, styles.confettiFall5, styles.confettiFall6,
              ];
              const isRound = i % 3 !== 0;
              const w = isRound ? 8 + (i % 5) * 2 : 6 + (i % 4) * 2;
              const h = isRound ? w : 8 + (i % 5) * 2;
              return (
                <div
                  key={`confetti-${i}`}
                  className={`${styles.confettiPiece} ${fallClasses[i % 6]}`}
                  style={{
                    '--left': `${((i * 7.3) % 100).toFixed(1)}%`,
                    '--color': colors[i % colors.length],
                    '--duration': `${(2.5 + ((i * 3) % 11) / 10).toFixed(1)}s`,
                    '--delay': `${(((i * 13) % 100) / 100).toFixed(2)}s`,
                    '--w': `${w}px`,
                    '--h': `${h}px`,
                    '--radius': isRound ? '50%' : '2px',
                  }}
                />
              );
            })}

            {/* Firework burst group 1 - left */}
            <div className={`${styles.fireworkGroup1}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst1-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Firework burst group 2 - right */}
            <div className={`${styles.fireworkGroup2}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst2-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Firework burst group 3 - center */}
            <div className={`${styles.fireworkGroup3}`} style={{ position: 'absolute' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={`burst3-${i}`} className={styles.fireworkBurst} />
              ))}
            </div>

            {/* Sparkle dots */}
            {Array.from({ length: 8 }, (_, i) => (
              <div key={`sparkle-${i}`} className={styles.sparkle} />
            ))}

            {/* Firework rockets + bursts from bottom — fewer on mobile */}
            {(window.innerWidth <= 767 ? [
              { left: '15%', rx: 30, ry: 72, delay: 3.6 },
              { left: '40%', rx: 8, ry: 75, delay: 3.7 },
              { left: '60%', rx: -8, ry: 75, delay: 3.55 },
              { left: '85%', rx: -30, ry: 72, delay: 3.4 },
            ] : [
              { left: '5%', rx: 40, ry: 65, delay: 3.4 },
              { left: '15%', rx: 30, ry: 72, delay: 3.6 },
              { left: '25%', rx: 18, ry: 68, delay: 3.5 },
              { left: '35%', rx: 8, ry: 75, delay: 3.7 },
              { left: '45%', rx: 3, ry: 70, delay: 3.45 },
              { left: '55%', rx: -3, ry: 70, delay: 3.55 },
              { left: '65%', rx: -8, ry: 75, delay: 3.65 },
              { left: '75%', rx: -18, ry: 68, delay: 3.5 },
              { left: '85%', rx: -30, ry: 72, delay: 3.4 },
              { left: '95%', rx: -40, ry: 65, delay: 3.6 },
            ]).map((r, idx) => (
              <React.Fragment key={`fw-${idx}`}>
                <div
                  className={styles.rocketWrap}
                  style={{
                    left: r.left,
                    '--rx': `${r.rx}px`,
                    '--ry': `-${r.ry}vh`,
                    '--rdelay': `${r.delay}s`,
                  }}
                >
                  <div className={styles.rocketTrail} />
                </div>
                <div
                  className={styles.burstWrap}
                  style={{
                    left: r.left,
                    bottom: `${r.ry}vh`,
                    '--bx': `${r.rx}px`,
                    '--bdelay': `${r.delay + 1.3}s`,
                  }}
                >
                  {Array.from({ length: window.innerWidth <= 767 ? 6 : 12 }, (_, i) => (
                    <div key={i} className={styles.burstSpark} />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        {/* Dark overlay */}
        {showGiftPopup && (
          <div
            className={`${styles.giftOverlay} ${styles.giftOverlayVisible}`}
          />
        )}
        {/* Gift popup - celebration slides up, password fields appear below */}
        {showGiftPopup && (
        <div className={`${styles.giftPopup} ${styles.giftPopupVisible}`}>
          {/* The popup covers the viewport, so it carries the login backdrop
              itself rather than letting the page's rays show through. */}
          <RaysBackdrop className={styles.giftRaysLayer} />
          {/* Open in App popup (mobile only, after celebration fades) */}
          {showOpenAppPopup && (
            <div className={styles.openAppOverlay}>
              <div className={styles.openAppCard}>
                <div className={styles.openAppEmoji}>🎁</div>
                <h2 className={styles.openAppTitle}>Open in Projector App</h2>
                <p className={styles.openAppDescription}>
                  For the best experience, open this gift in the Projector app
                </p>
                <button
                  type="button"
                  className={cn(theme.primary, styles.openAppButton)}
                  onClick={handleOpenApp}
                >
                  Open App
                </button>
                <button
                  type="button"
                  className={cn(theme.linkButton, styles.openAppDismiss)}
                  onClick={handleContinueInBrowser}
                >
                  Continue in Browser
                </button>
              </div>
            </div>
          )}
          <div className={`${styles.giftPopupContent} ${giftContentStep === 'password' ? styles.giftContentCompact : ''}`}>
            {/* Celebration header - always visible, slides up in password step */}
            <div className={styles.giftIconWrapper}>
              {Array.from({ length: 10 }, (_, i) => (
                <div key={`cracker-${i}`} className={styles.crackerParticle} />
              ))}
              <div className={styles.boxGlow} />
              <div className={styles.giftIcon}>🎁</div>
            </div>
            <h1 className={styles.giftTitle}>You Have Received Projector Memories!</h1>
            <p className={styles.giftSubtitle}>{`${giftFromName} has sent you a gift!`}</p>
            <p className={styles.giftDescription}>
              Never miss a memory
            </p>

            {/* Email section + button - only visible during celebration step.
                Sits in the login card so the confirm step matches /newSign-in. */}
            {giftContentStep === 'celebration' && (
              <div className={styles.giftCard}>
                <div className={styles.giftEmailWrap}>
                  <label className={styles.giftEmailLabel}>Confirm your email to continue</label>
                  <div className={styles.giftEmailInputRow}>
                    <input
                      type="email"
                      className={styles.giftEmailInput}
                      value={confirmEmailInput}
                      onFocus={() => { if (!videoPlayStarted.current) { setUserInteracted(true); setShowProjectorVideo(true); } }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setConfirmEmailInput(val);
                        setEmailMismatch(false);
                        if (val) {
                          setEmailVerifying(true);
                          debouncedEmailMatch(val);
                        } else {
                          setEmailVerifying(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && emailConfirmed) {
                          e.preventDefault();
                          handleDismissGiftPopup();
                        }
                      }}
                      placeholder="Enter your email address"
                    />
                    {!(isExistingUser && isGiftFlow) && emailVerifying && (
                      <span className={styles.giftEmailSpinner}>
                        <span className={styles.giftEmailSpinnerRing} />
                      </span>
                    )}
                    {!(isExistingUser && isGiftFlow) && !emailVerifying && emailConfirmed && (
                      <span className={styles.giftEmailTick}>&#10003;</span>
                    )}
                  </div>
                  {!(isExistingUser && isGiftFlow) && emailMismatch && !emailVerifying && (
                    <p className={styles.giftEmailError}>Email does not match. Please try again.</p>
                  )}
                </div>
                <button
                  type="button"
                  className={cn(theme.primary, styles.giftContinueButton)}
                  onClick={handleDismissGiftPopup}
                  disabled={isExistingUser && isGiftFlow ? (!confirmEmailInput || !isValidEmail(confirmEmailInput)) : !emailConfirmed}
                >
                  Let&apos;s Get Started
                </button>
              </div>
            )}

            {/* Password fields - appear below celebration content */}
            {giftContentStep === 'password' && (
              <div className={cn(styles.giftCard, styles.giftPasswordFields, styles.giftContentFadeIn)}>
                {noPassword === false ? (
                  /* Password already set - show message and login button inside fireworks popup */
                  (
<div className={styles.alreadySet}>
                    <h2 className={theme.title}>
                      Password Already Set
                    </h2>
                    <p className={theme.subtitle}>
                      You already have a password. Please login with your existing credentials.
                    </p>
                    <Button
                      className={theme.primary}
                      blockText
                      isBlue
                      isFullWidth
                      isLarge
                      onClick={() => navigate('/newSign-in')}
                    >
                      Go to Login
                    </Button>
</div>
)
                ) : (
                  /* Password not set - show password form */
                  (
<>
                    {/* Bottom-anchored like every other toast in the gift flow
                        (gift-flow/index.jsx, gift-content). This one renders
                        inside the fixed, transformed .giftPopup, which is its
                        containing block — and that spans the viewport while the
                        popup is open, so `bottom` lands where it does elsewhere. */}
                    <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} placement="bottom" />
                    <form onSubmit={handleNext}>
                      <div className={theme.field}>
                        <Input
                          label={isExistingUser && isGiftFlow ? 'Enter Password' : 'Create Password'}
                          type="password"
                          automaticFocus
                          value={password}
                          onInput={handlePasswordInput}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (isExistingUser && isGiftFlow) {
                                e.target.form?.requestSubmit();
                              } else {
                                confirmPasswordRef.current?.focus();
                              }
                            }
                          }}
                          onPaste={(e) => {
                            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                            if (pastedText.length > 30) {
                              e.preventDefault();
                            }
                            const totalLength = e.target.value.length + pastedText.length;
                            if (totalLength > 30) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const inputPassword = e.target.value;
                            if (inputPassword.length > 30) {
                              const trimmedText = inputPassword.slice(0, 30);
                              e.target.value = trimmedText;
                              setPassword(trimmedText);
                            }
                          }}
                          placeholder="Enter password"
                          minLength={12}
                          maxLength={30}
                          required
                        />
                      </div>

                      {!(isExistingUser && isGiftFlow) && (
                      <div className={cn(theme.field, theme.fieldStack)}>
                        <Input
                          label="Confirm Password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => {
                            const val = e.target.value;
                            setConfirmPassword(val);
                            debouncedPasswordMatch(val, password);
                          }}
                          placeholder="Confirm password"
                          required
                          inputRef={confirmPasswordRef}
                          helperText={
                            confirmPassword
                            && !passwordsMatch && (
                              <span className={authStyles.errorText}>
                                Passwords do not match
                              </span>
                            )
                          }
                        />
                      </div>
                      )}

                      {!(isExistingUser && isGiftFlow) && (
                        <PasswordRules password={password} />
                      )}

                      <Button
                        className={theme.primary}
                        blockText
                        isBlue
                        isFullWidth
                        isLarge
                        submit
                        isDisabled={
                          isExistingUser && isGiftFlow
                            ? (submitLoading || !password)
                            : (submitLoading
                              || !password
                              || !confirmPassword
                              || !passwordsMatch
                              || !((passwordHealth === 'strong') || (passwordHealth === 'medium')))
                        }
                      >
                        {submitLoading ? (isExistingUser && isGiftFlow ? 'Logging in...' : 'Updating your Profile...') : (isExistingUser && isGiftFlow ? 'Login' : 'Next')}
                      </Button>
                    </form>
</>
)
                )}
              </div>
            )}
          </div>
        </div>
        )}
        {/* Projector intro video — mounted early to preload, visible only when ready */}
        {isGiftFlow && !isExistingUser && (
          <div
            className={styles.projectorVideoOverlay}
            style={(showProjectorVideo && videoReady) ? {} : { visibility: 'hidden', position: 'fixed', pointerEvents: 'none' }}
          >
            <div className={styles.projectorVideoModal}>
              <button
                type="button"
                className={styles.projectorVideoSkip}
                onClick={handleDismissProjectorVideo}
              >
                Skip
              </button>
              <video
                className={styles.projectorVideoPlayer}
                src={projectorVideoUrl}
                playsInline
                preload="auto"
                onCanPlayThrough={() => {
                  setVideoReady(true);
                }}
                onEnded={handleDismissProjectorVideo}
                style={{ width: '100%', height: 'auto' }}
                ref={videoRef}
              />
              {showProjectorVideo && videoReady && (
                <button
                  type="button"
                  className={styles.projectorVideoMuteToggle}
                  onClick={() => {
                    if (videoRef.current) {
                      const newMuted = !videoMuted;
                      Object.assign(videoRef.current, { muted: newMuted });
                      setVideoMuted(newMuted);
                    }
                  }}
                >
                  {videoMuted ? '🔇' : '🔊'}
                </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  // Show loader while checking token
  if (isLoading) {
    return (
      <AuthPage className={theme.page} contentClassName={theme.content}>
        {/* {renderEmailConfirmScreen()} */}
        {renderGiftCelebration()}
        {!showGiftPopup && (
          <>
            <RaysBackdrop />
            <div className={cn(theme.card, styles.card, styles.loaderCard)}>
              <svg className="loading-circle" viewBox="0 0 50 50" style={{ width: '50px', height: '50px' }}>
                <circle
                  className="circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
            </div>
          </>
        )}
      </AuthPage>
    );
  }

  // Show message if user already has a password
  if (noPassword === false) {
    return (
      <AuthPage className={theme.page} contentClassName={theme.content}>
        {/* {renderEmailConfirmScreen()} */}
        {renderGiftCelebration()}
        {!showGiftPopup && (
          <>
            <RaysBackdrop />
            <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} placement="bottom-edge" />
            <div className={cn(theme.card, theme.stepIn, styles.card)}>
              <div className={theme.head}>
                <h1 className={theme.title}>
                  Password Already Set
                </h1>
                <p className={theme.subtitle}>
                  You already have a password. Please login with your existing credentials.
                </p>
              </div>
              <Button
                className={theme.primary}
                blockText
                isBlue
                isFullWidth
                isLarge
                onClick={() => navigate('/newSign-in')}
              >
                Go to Login
              </Button>
            </div>
          </>
        )}
      </AuthPage>
    );
  }

  // Show set password form if no_password is true
  return (
    <AuthPage className={theme.page} contentClassName={theme.content}>
      {/* {renderEmailConfirmScreen()} */}
      {renderGiftCelebration()}
      {!showGiftPopup && (
        <>
          <RaysBackdrop />
          {submitLoading && <RotatingLoader dark />}
          <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} placement="bottom-edge" />
          <div className={cn(theme.card, theme.stepIn, styles.card)}>
            <div className={theme.head}>
              <h1 className={cn(theme.title, styles.titleOneLine)}>
                Your Projector Account Is Here
              </h1>
              <p className={theme.subtitle}>
                Create a secure password for your account
              </p>
            </div>

            <form onSubmit={handleNext}>
              <div className={theme.field}>
                <Input
                  label="Create Password"
                  type="password"
                  automaticFocus
                  value={password}
                  onInput={handlePasswordInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmPasswordRef.current?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                    if (pastedText.length > 30) {
                      e.preventDefault();
                    }
                    const totalLength = e.target.value.length + pastedText.length;
                    if (totalLength > 30) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const inputPassword = e.target.value;
                    if (inputPassword.length > 30) {
                      const trimmedText = inputPassword.slice(0, 30);
                      e.target.value = trimmedText;
                      setPassword(trimmedText);
                    }
                  }}
                  placeholder="Enter password"
                  minLength={12}
                  maxLength={30}
                  required
                />
              </div>

              <div className={cn(theme.field, theme.fieldStack)}>
                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setConfirmPassword(val);
                    if (val && password && val !== password) {
                      setSnackbar({ isVisible: true, type: 'error', message: 'Passwords do not match' });
                    } else {
                      setSnackbar(defaultSnackbar);
                    }
                  }}
                  placeholder="Confirm password"
                  required
                  inputRef={confirmPasswordRef}
                  helperText={
                    confirmPassword
                    && !passwordsMatch && (
                      <span className={authStyles.errorText}>
                        Passwords do not match
                      </span>
                    )
                  }
                />
              </div>

              <PasswordRules password={password} />

              <Button
                className={theme.primary}
                blockText
                isBlue
                isFullWidth
                isLarge
                submit
                isDisabled={
                  submitLoading
                  || !password
                  || !confirmPassword
                  || !passwordsMatch
                  || !((passwordHealth === 'strong') || (passwordHealth === 'medium'))
                }
              >
                {submitLoading ? 'Updating your Profile...' : 'Next'}
              </Button>
            </form>
          </div>
        </>
      )}
    </AuthPage>
  );
};
